import { lazy, Suspense, useState } from 'react';
import type { Mark } from '../../data/types';
import type { NewMark } from '../../db/repos/marks';
import { formatTime } from '../../lib/time';
import { Button } from '../../ui/Button';
import { FilterChip } from '../../ui/FilterChip';
import { Readout } from '../../ui/Readout';
import { StepperSheet } from '../../ui/StepperSheet';
import screen from '../screen.module.css';
import styles from './MarkEditor.module.css';

// The picker carries the key-by-ear drone and its audio, so it loads with the editor, not with Dig (SPEC F14)
const KeyPicker = lazy(() => import('../harmony/KeyPicker'));
const KINDS: { value: Mark['kind']; label: string }[] = [
  { value: 'loop', label: 'Loop' },
  { value: 'stab', label: 'Stab' },
  { value: 'bass', label: 'Bass alone' },
  { value: 'vocal', label: 'Vocal' },
  { value: 'break', label: 'Break' },
];

export interface MarkEditorProps {
  videoId: string;
  inS: number;
  outS: number;
  bpm?: number;
  durationS: number;
  /** The active project's name: new marks link to it by default (SPEC §4) */
  projectName?: string;
  projectId?: string;
  /** Editing a saved mark (reopened from Marks): its other fields fill the form */
  initial?: Mark;
  onSave: (mark: NewMark) => void;
  onCancel: () => void;
}

type Field = 'in' | 'out' | 'bpm';

/** The inline mark editor (DECISIONS): replaces the control rows under the title; the player stays visible. */
export function MarkEditor(p: MarkEditorProps) {
  const [inS, setIn] = useState(p.inS);
  const [outS, setOut] = useState(p.outS);
  const [bpm, setBpm] = useState<number | undefined>(p.bpm);
  const [keyRoot, setKeyRoot] = useState<number | undefined>(p.initial?.keyRoot);
  const [minor, setMinor] = useState(p.initial?.minor ?? false);
  const [kind, setKind] = useState<Mark['kind']>(p.initial?.kind ?? 'loop');
  const [note, setNote] = useState(p.initial?.note ?? '');
  const [link, setLink] = useState(p.initial ? Boolean(p.initial.projectId) : Boolean(p.projectId));
  const [field, setField] = useState<Field | null>(null);

  const max = Math.max(1, Math.floor(p.durationS) || 3600);
  const valid = outS > inS;

  const save = () =>
    p.onSave({
      videoId: p.videoId,
      inS,
      outS,
      bpm,
      keyRoot,
      minor: keyRoot === undefined ? undefined : minor,
      kind,
      note: note.trim() || undefined,
      projectId: link ? (p.initial?.projectId ?? p.projectId) : undefined,
    });

  return (
    <div className={styles.editor}>
      <h2 className="t-section">{p.initial ? 'Edit mark' : 'Save a mark'}</h2>

      <div className={styles.times}>
        <Readout label="In" value={formatTime(inS, true)} onClick={() => setField('in')} actionLabel="change" />
        <Readout label="Out" value={formatTime(outS, true)} onClick={() => setField('out')} actionLabel="change" />
        <Readout label="BPM" value={bpm === undefined ? '—' : String(bpm)} onClick={() => setField('bpm')} actionLabel="change" />
      </div>
      {!valid && <p className={`t-meta ${styles.problem}`} role="alert">Out has to come after In.</p>}

      <fieldset className={styles.group}>
        <legend className="t-label">Key</legend>
        <Suspense fallback={null}>
          <KeyPicker
            root={keyRoot}
            minor={minor}
            layout="scroll"
            onChange={(k) => {
              setKeyRoot(k?.keyRoot);
              if (k) setMinor(k.minor);
            }}
          />
        </Suspense>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className="t-label">Kind</legend>
        <div className={styles.chips}>
          {KINDS.map((k) => (
            <FilterChip key={k.value} selected={kind === k.value} onClick={() => setKind(k.value)}>{k.label}</FilterChip>
          ))}
        </div>
      </fieldset>

      <label className={styles.note}>
        <span className="t-label">Note</span>
        <textarea className={styles.text} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Keep the organ stab dry" />
      </label>

      {(p.initial?.projectId ?? p.projectId) && p.projectName && (
        <FilterChip selected={link} onClick={() => setLink((l) => !l)}>Link to {p.projectName}</FilterChip>
      )}

      <div className={`${styles.actions} ${screen.stickyBottom}`} data-bottom-actions>
        <Button variant="secondary" onClick={p.onCancel}>Cancel</Button>
        <Button variant="primary" disabled={!valid} onClick={save}>Save</Button>
      </div>

      {field === 'in' && (
        <StepperSheet open title="In" value={inS} min={0} max={max} step={0.1} format={(v) => formatTime(v, true)} nudge={(v, d) => Math.max(0, Math.round((v + d * 0.1) * 10) / 10)} onChange={setIn} onClose={() => setField(null)} />
      )}
      {field === 'out' && (
        <StepperSheet open title="Out" value={outS} min={0} max={max} step={0.1} format={(v) => formatTime(v, true)} nudge={(v, d) => Math.max(0, Math.round((v + d * 0.1) * 10) / 10)} onChange={setOut} onClose={() => setField(null)} />
      )}
      {field === 'bpm' && (
        <StepperSheet open title="BPM" value={bpm ?? 90} min={40} max={220} onChange={setBpm} onClose={() => setField(null)} />
      )}
    </div>
  );
}
